import type {
  CharityVehicle,
  CharityVehicleExpandable,
  CharityVehicleListOptions,
  ManifestItem,
  PaginatedResponse,
  Schedule,
} from '@types'
import { apiRequest, paginatedRequest, type ListOptions } from './base'

export const charityVehicleService = {
  /**
   * @roles charity_driver, charity, captain
   */
  async list<E extends CharityVehicleExpandable = never>(
    options: CharityVehicleListOptions & { expand?: E | E[] } = {}
  ): Promise<PaginatedResponse<CharityVehicle>> {
    return paginatedRequest('/charity_vehicles', {
      method: 'GET',
      params: options,
    })
  },

  /**
   * @roles charity_driver, charity, captain
   */
  async get<E extends CharityVehicleExpandable = never>(
    id: number,
    options: { expand?: E | E[] } = {}
  ): Promise<CharityVehicle> {
    return apiRequest(`/charity_vehicles/${id}`, {
      method: 'GET',
      params: options.expand
        ? {
            expand: Array.isArray(options.expand)
              ? options.expand.join(',')
              : options.expand,
          }
        : {},
    })
  },

  /**
   * @roles charity, captain
   */
  async create(data: Partial<CharityVehicle>): Promise<CharityVehicle> {
    return apiRequest('/charity_vehicles', { method: 'POST', body: data })
  },

  /**
   * @roles charity_driver, charity, captain
   */
  async update(
    id: number,
    data: Partial<CharityVehicle>
  ): Promise<CharityVehicle> {
    return apiRequest(`/charity_vehicles/${id}`, { method: 'PUT', body: data })
  },

  /**
   * @roles charity, captain
   */
  async delete(id: number): Promise<void> {
    return apiRequest(`/charity_vehicles/${id}`, { method: 'DELETE' })
  },

  /**
   * @roles charity, captain
   */
  async restore(id: number): Promise<CharityVehicle> {
    return apiRequest(`/charity_vehicles/${id}/restore`, { method: 'POST' })
  },

  // Nested schedules resource
  schedules: {
    async list(
      charity_vehicleId: number,
      options: ListOptions = {}
    ): Promise<PaginatedResponse<Schedule>> {
      return paginatedRequest(
        `/charity_vehicles/${charity_vehicleId}/schedules`,
        { method: 'GET', params: options }
      )
    },

    async get(charity_vehicleId: number, id: number): Promise<Schedule> {
      return apiRequest(
        `/charity_vehicles/${charity_vehicleId}/schedules/${id}`,
        { method: 'GET' }
      )
    },

    async create(
      charity_vehicleId: number,
      data: Partial<Schedule>
    ): Promise<Schedule> {
      return apiRequest(`/charity_vehicles/${charity_vehicleId}/schedules`, {
        method: 'POST',
        body: data,
      })
    },

    async update(
      charity_vehicleId: number,
      id: number,
      data: Partial<Schedule>
    ): Promise<Schedule> {
      return apiRequest(
        `/charity_vehicles/${charity_vehicleId}/schedules/${id}`,
        { method: 'PUT', body: data }
      )
    },

    async delete(charity_vehicleId: number, id: number): Promise<void> {
      return apiRequest(
        `/charity_vehicles/${charity_vehicleId}/schedules/${id}`,
        { method: 'DELETE' }
      )
    },
  },

  //Nested manifest resource
  manifest: {
    async get(
      charity_vehicleId: number,
      date: string
    ): Promise<ManifestItem[]> {
      return apiRequest(
        `/charity_vehicles/${charity_vehicleId}/manifests/${date}`,
        { method: 'GET' }
      )
    },
  },
}
